import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuth, useLocale, useSettings } from '../../context';
import { useCasinoBadges } from '../../hooks/useCasinoBadges';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  gameSupportsDemo,
  markGameIconFailed,
  markGameIconLoaded,
  isGameIconLoaded,
  sideGameImageCandidates,
  gameIconPlaceholder,
  warmGameIcon,
} from '../../data/casinoGames';
import GameCardPlaceholder from './GameCardPlaceholder';
import {
  formatGameDisplayName,
  formatProviderDisplayName,
  gameCardNameSizes,
  gameCardStyleVars,
  prepareGameTitle,
} from '../../utils/gameCardTheme';
import { isCategoryBadgeKind } from '../../utils/deriveCasinoCardTags';
import { claimGameCardOverlay, subscribeGameCardOverlay } from '../../utils/gameCardOverlay';
import { prefetchCasinoGameChunk, prefetchCasinoLaunch } from '../../utils/casinoGamePrefetch';
import { pushLastPlayedId, readFavoriteIds, writeFavoriteIds } from '../../utils/casinoLocalLists';

function IconStar({ filled = false }) {
  return (
    <svg className="game-card__icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M13.7289 10.7089C13.5381 10.8938 13.4504 11.1613 13.4939 11.4236L14.1489 15.0486C14.2041 15.3558 14.0745 15.6668 13.8173 15.8443C13.5653 16.0285 13.2301 16.0506 12.9553 15.9033L9.69203 14.2013C9.57856 14.1409 9.45257 14.1085 9.32363 14.1048H9.12396C9.0547 14.1151 8.98692 14.1372 8.92503 14.1711L5.66103 15.8812C5.49968 15.9622 5.31695 15.991 5.13791 15.9622C4.70173 15.8797 4.4107 15.4642 4.48216 15.0258L5.13791 11.4007C5.18138 11.1362 5.0937 10.8673 4.90287 10.6794L2.24231 8.10065C2.0198 7.88476 1.94244 7.56058 2.04412 7.26807C2.14285 6.9763 2.39483 6.76337 2.69913 6.71547L6.36099 6.18425C6.63949 6.15551 6.88411 5.98605 7.00936 5.73554L8.62294 2.42734C8.66125 2.35366 8.71062 2.28588 8.7703 2.22841L8.83661 2.17683C8.87124 2.13852 8.91103 2.10683 8.95523 2.08105L9.03555 2.05158L9.1608 2H9.47099C9.74802 2.02873 9.9919 2.19451 10.1194 2.44208L11.7543 5.73554C11.8722 5.97647 12.1013 6.14372 12.3659 6.18425L16.0277 6.71547C16.3372 6.75968 16.5958 6.97335 16.6982 7.26807C16.7947 7.56352 16.7115 7.88771 16.4845 8.10065L13.7289 10.7089Z"
        stroke="currentColor"
        fill={filled ? 'currentColor' : 'none'}
      />
    </svg>
  );
}

function IconPopout() {
  return (
    <svg className="game-card__icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.5 2.25a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0V4.06l-5.47 5.47a.75.75 0 1 1-1.06-1.06l5.47-5.47H11.25a.75.75 0 0 1-.75-.75ZM3.75 4.5A1.5 1.5 0 0 0 2.25 6v8.25A1.5 1.5 0 0 0 3.75 15.75H12a1.5 1.5 0 0 0 1.5-1.5V9a.75.75 0 0 1 1.5 0v5.25A3 3 0 0 1 12 17.25H3.75A3 3 0 0 1 .75 14.25V6A3 3 0 0 1 3.75 3H9a.75.75 0 0 1 0 1.5H3.75Z"
      />
    </svg>
  );
}

function GameCaption({ name, compact = false }) {
  const rootRef = useRef(null);
  const [lines, setLines] = useState(() => prepareGameTitle(name, 116, { compact }));

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const sync = () => {
      const width = Math.max(1, (el.clientWidth || 116) - 0.5);
      setLines(prepareGameTitle(name, width, { compact: compact || width <= 118 }));
    };
    sync();

    if (compact || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, [name, compact]);

  const primary = lines[0] || { value: '', fontSize: 20.9 };
  const secondary = lines[1] || { value: '', fontSize: 17.9 };
  const tertiary = lines[2] || { value: '', fontSize: 17.9 };

  return (
    <div className="game-card__caption-game" lang="en" ref={rootRef}>
      {primary.value ? (
        <span className="game-card__caption-primary" style={{ fontSize: `${primary.fontSize}px` }}>
          {primary.value}
        </span>
      ) : null}
      {secondary.value ? (
        <span className="game-card__caption-secondary" style={{ fontSize: `${secondary.fontSize}px` }}>
          {secondary.value}
        </span>
      ) : null}
      {tertiary.value ? (
        <span className="game-card__caption-secondary" style={{ fontSize: `${tertiary.fontSize}px` }}>
          {tertiary.value}
        </span>
      ) : null}
    </div>
  );
}

function syncGameCardMetrics(cardEl, labelEl, variant, baseCardWidth) {
  if (!cardEl || !labelEl) return;

  const width = cardEl.clientWidth;
  const height = cardEl.clientHeight;
  const labelHeight = labelEl.offsetHeight;

  if (width > 0) {
    cardEl.style.setProperty('--game-card-w', `${width}px`);
  }
  if (height > 0) {
    cardEl.style.setProperty('--game-card-h', `${height}px`);
  }
  if (labelHeight > 0) {
    cardEl.style.setProperty('--game-card-label-h', `${labelHeight}px`);
  }

  const referenceWidth = baseCardWidth || (variant === 'compact' ? 117 : 221);
  if (width <= 0) return;
  if (width <= 132) return;

  const scaleReference = variant === 'page' ? 147 : referenceWidth;
  if (scaleReference <= 0) return;

  const scale = Math.max(0.72, Math.min(1.15, width / scaleReference));
  const fonts = gameCardNameSizes(variant);
  cardEl.style.setProperty('--game-card-name-primary', `${(fonts.primary * scale).toFixed(2)}px`);
  cardEl.style.setProperty('--game-card-name-secondary', `${(fonts.secondary * scale).toFixed(2)}px`);
  cardEl.style.setProperty('--game-card-provider-size', `${(fonts.provider * scale).toFixed(2)}px`);
}

const MAXLINE_TAG_KINDS = new Set(['tournament', 'new', 'x50000', 'x15000', 'fs', 'hot', 'top', 'popular', 'best']);

function resolveBadges(game, { tournamentBadge = false, homeTags = null } = {}) {
  if (Array.isArray(homeTags) && homeTags.length > 0) {
    const list = homeTags
      .map((tag) => (typeof tag === 'string' ? { kind: tag } : tag))
      .filter((tag) => tag && tag.kind);
    if (tournamentBadge && !list.some((tag) => tag.kind === 'tournament')) {
      list.push({ kind: 'tournament' });
    }
    if (game.freespin && !list.some((tag) => tag.kind === 'fs')) list.push({ kind: 'fs' });
    return list;
  }
  const badges = [];
  if (game.badge && !isCategoryBadgeKind(game.badge)) {
    badges.push({ kind: String(game.badge) });
  }
  if (tournamentBadge && !badges.some((tag) => tag.kind === 'tournament')) {
    badges.push({ kind: 'tournament' });
  }
  if (game.freespin) badges.push({ kind: 'fs' });
  return badges;
}

function FlameHeatIcon({ id, big = false }) {
  const gradId = `${id}-grad`;
  if (big) {
    return (
      <svg className="game-card__heat-icon" xmlns="http://www.w3.org/2000/svg" width="63" height="81" viewBox="0 0 63 81" fill="none" aria-hidden="true">
        <path
          fill={`url(#${gradId})`}
          stroke="#fff"
          strokeWidth="3"
          d="M24.251 1.613c.853-.236 1.794-.081 2.74.257l.367.14c1.914.775 4.897 2.593 7.921 4.897 3.253 2.478 6.724 5.649 9.14 8.96 3.365 4.607 5.64 9.115 7.077 12.47.425.994.775 1.89 1.06 2.657.386-.699.74-1.465.95-2.218.205-.725.339-1.375.52-2.098.154-.621.366-1.4.733-1.989.196-.314.512-.7 1.011-.939a2.18 2.18 0 0 1 1.61-.096c.843.266 1.592.993 2.273 1.797 2.655 3.133 6.57 8.973 8.79 14.575 2.028 5.114 2.751 10.116 2.972 13.826a46 46 0 0 1 .053 4.488 32 32 0 0 1-.105 1.615l-.007.071C70.55 78.131 55.195 92.5 36.447 92.5 17.174 92.5 1.5 76.833 1.5 58.537c0-2.466-.025-7.356 3.694-15.474 2.175-4.746 3.467-7.58 4.19-10.143.107-.378.264-.905.49-1.392.201-.433.586-1.13 1.307-1.52a2.23 2.23 0 0 1 1.375-.247c.476.066.89.273 1.232.52.656.475 1.269 1.256 1.882 2.27.639 1.057 1.02 2.25 1.25 3.28.24-.244.498-.509.765-.799a32 32 0 0 0 5.31-7.874c2.392-5.043 2.603-8.985 2.103-12.159-.254-1.607-.696-3.052-1.172-4.392-.394-1.107-.875-2.3-1.224-3.337l-.14-.434a8 8 0 0 1-.332-1.597c-.055-.56-.05-1.27.2-1.941.279-.743.864-1.42 1.821-1.685Z"
        />
        <defs>
          <linearGradient id={gradId} x1="36.498" x2="36.498" y1="91.065" y2="3.067" gradientUnits="userSpaceOnUse">
            <stop stopColor="red" />
            <stop offset="1" stopColor="#f30" />
          </linearGradient>
        </defs>
      </svg>
    );
  }
  return (
    <svg className="game-card__heat-icon" xmlns="http://www.w3.org/2000/svg" width="42" height="54" viewBox="0 0 42 54" fill="none" aria-hidden="true">
      <path
        fill={`url(#${gradId})`}
        stroke="#fff"
        strokeWidth="2"
        d="M14.013 1.07c.46-.127.954-.068 1.437.08l.207.069.213.082c1.106.448 2.81 1.491 4.528 2.803 1.851 1.412 3.833 3.223 5.215 5.12a36.7 36.7 0 0 1 4.517 8.319c.152-.305.284-.624.372-.937.115-.41.189-.77.293-1.188.087-.35.211-.815.433-1.172.12-.192.318-.437.637-.59.348-.165.71-.16 1.023-.061.522.165.971.609 1.358 1.066 1.514 1.79 3.743 5.122 5.01 8.323 1.157 2.924 1.57 5.782 1.695 7.9a26 26 0 0 1 .03 2.565 19 19 0 0 1-.06.926q-.002.029-.005.047C40.449 44.782 31.676 53 20.971 53 9.958 53 1 44.034 1 33.556c0-1.414-.013-4.216 2.108-8.854 1.235-2.7 1.963-4.299 2.37-5.742a5.5 5.5 0 0 1 .285-.812c.117-.252.352-.687.805-.932.256-.138.555-.201.872-.157.302.041.558.172.766.323.397.287.757.751 1.108 1.333.32.528.525 1.112.663 1.641q.117-.12.24-.254a18 18 0 0 0 2.988-4.438c1.344-2.839 1.46-5.049 1.18-6.823-.141-.901-.389-1.713-.658-2.47-.25-.707-.575-1.505-.775-2.148a5 5 0 0 1-.194-.937c-.032-.328-.03-.757.123-1.168a1.66 1.66 0 0 1 1.132-1.048Z"
      />
      <defs>
        <linearGradient id={gradId} x1="20.999" x2="20.999" y1="52.037" y2="2.038" gradientUnits="userSpaceOnUse">
          <stop stopColor="#ff310d" />
          <stop offset="1" stopColor="#fc9502" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function CasinoGameCard({
  game,
  luckyId,
  isLuckyHighlight = false,
  onNavigate,
  mobileTapToPlay = false,
  variant = 'page',
  cardWidth,
  eagerImage = false,
  iconSize,
  hotBadge = false,
  heatPercent = null,
  heatBig = false,
  tournamentBadge = false,
  homeTags = null,
  spinnerWhileLoading = true,
}) {
  const _locale = useLocale();
  const tr = _locale.t;
  const { user, token } = useAuth();
  const { settings } = useSettings();
  const { resolveBadgeDisplay } = useCasinoBadges();
  const isTouchUi = useIsMobile();
  const rootRef = useRef(null);
  const labelRef = useRef(null);
  const overlayIdRef = useRef(`gc-${game?.id ?? 'x'}-${Math.random().toString(36).slice(2, 8)}`);
  const resolvedIconSize = iconSize || 'md';
  const [visible, setVisible] = useState(false);
  const [candidateIdx, setCandidateIdx] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);
  const [forcePlaceholder, setForcePlaceholder] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [tapOpen, setTapOpen] = useState(false);
  const candidates = sideGameImageCandidates(game, { size: resolvedIconSize });
  const imgSrc = (!spinnerWhileLoading && forcePlaceholder)
    ? gameIconPlaceholder()
    : (candidates[candidateIdx] || candidates[0] || null);
  const [loaded, setLoaded] = useState(() => Boolean(imgSrc && isGameIconLoaded(imgSrc)));
  const badges = resolveBadges(game, { tournamentBadge, homeTags: homeTags || game?.homeTags });
  const themeVars = gameCardStyleVars(cardWidth || (variant === 'compact' ? 117 : 221), variant);
  const compactCaption = variant === 'compact' || (cardWidth && cardWidth <= 132);
  const gameId = game?.id != null ? String(game.id) : '';
  const overlayOpen = Boolean(isLuckyHighlight || tapOpen);

  useEffect(() => {
    setVisible(Boolean(eagerImage));
    setCandidateIdx(0);
    setImgFailed(false);
    setForcePlaceholder(false);
    setRetryTick(0);
    setTapOpen(false);
    const first = sideGameImageCandidates(game, { size: resolvedIconSize })[0];
    setLoaded(Boolean(first && isGameIconLoaded(first)));
    if (eagerImage && first) warmGameIcon(first);
  }, [game?.id, eagerImage, resolvedIconSize]);

  useEffect(() => {
    if (!isTouchUi) {
      setTapOpen(false);
      return undefined;
    }
    return subscribeGameCardOverlay(overlayIdRef.current, () => setTapOpen(false));
  }, [isTouchUi]);

  useEffect(() => {
    if (!isTouchUi || !tapOpen) return undefined;
    claimGameCardOverlay(overlayIdRef.current);
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      setTapOpen(false);
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', onDoc, true);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onDoc, true);
    };
  }, [isTouchUi, tapOpen]);

  useEffect(() => {
    if (!gameId) {
      setIsFavorite(false);
      return undefined;
    }
    setIsFavorite(readFavoriteIds().includes(gameId));
    const onStorage = (e) => {
      if (e.key && e.key !== FAV_STORAGE_KEY) return;
      setIsFavorite(readFavoriteIds().includes(gameId));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [gameId]);

  useEffect(() => {
    const cardEl = rootRef.current;
    const labelEl = labelRef.current;
    if (!cardEl || !labelEl) return undefined;

    const sync = () => syncGameCardMetrics(cardEl, labelEl, variant, cardWidth);
    sync();

    if (compactCaption || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(sync);
    observer.observe(cardEl);
    observer.observe(labelEl);
    return () => observer.disconnect();
  }, [game?.id, variant, cardWidth, compactCaption]);

  useEffect(() => {
    if (eagerImage) {
      setVisible(true);
      return undefined;
    }
    const el = rootRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const scrollRoot = el.closest(
      '.casino-page__content-scroll, .casino-front__mobile-grid, .casino-front__grid-wrap, .game-grid__viewport, .home-slots-carousel__viewport',
    );
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      {
        root: scrollRoot || null,
        rootMargin: scrollRoot ? '48px 32px' : '64px 32px',
        threshold: 0.01,
      },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [game?.id, eagerImage]);

  const openGame = (demo) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!game?.id || !onNavigate) return;
    setTapOpen(false);
    pushLastPlayedId(game.id);
    prefetchCasinoGameChunk();
    prefetchCasinoLaunch({
      gameId: String(game.id),
      isDemo: Boolean(demo),
      token,
      locale: _locale.locale,
      userId: user?.id,
    });
    onNavigate('casino', { casinoGameId: String(game.id), casinoGameDemo: demo });
  };

  const toggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!gameId) return;
    const next = readFavoriteIds().filter((id) => id !== gameId);
    if (!isFavorite) next.push(gameId);
    writeFavoriteIds(next);
    setIsFavorite(!isFavorite);
  };

  const openPopout = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!gameId) return;
    pushLastPlayedId(gameId);
    window.open(`/casino/game/${gameId}`, '_blank', 'noopener,noreferrer');
  };

  const showImage = loaded && Boolean(imgSrc);
  const showDemo = gameSupportsDemo(game);
  const showHeat = typeof heatPercent === 'number' && Number.isFinite(heatPercent);
  const heatValue = showHeat ? Math.floor(heatPercent) : null;
  const heatId = `gc-heat-${game?.id ?? 'x'}`;
  const isHeatBig = showHeat && (heatBig || heatValue >= 150);

  const onCardActivate = (e) => {
    if (e.target.closest('.game-card__buttons')) return;
    if (isTouchUi) {
      e.preventDefault();
      e.stopPropagation();
      setTapOpen((open) => {
        const next = !open;
        if (next) claimGameCardOverlay(overlayIdRef.current);
        return next;
      });
      return;
    }
    openGame(false)(e);
  };

  return (
    <div
      className={`game-card game-card--${variant}${isTouchUi || mobileTapToPlay ? ' game-card--mobile-tap' : ''}${tapOpen ? ' is-tap-open' : ''}${isLuckyHighlight ? ' game-card--lucky' : ''}${hotBadge || showHeat ? ' game-card--hot' : ''}${isHeatBig ? ' game-card--hot-featured' : ''}`}
      data-lucky-id={luckyId}
      ref={rootRef}
      style={themeVars}
      onClick={onCardActivate}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCardActivate(e); }}
      role="button"
      tabIndex={0}
      aria-expanded={isTouchUi ? overlayOpen : undefined}
    >
      <div
        className={`game-card__background${showImage ? ' is-loaded' : ''}`}
        style={{ backgroundImage: showImage ? `url(${imgSrc})` : undefined }}
      >
        {!showImage && (
          (spinnerWhileLoading || !forcePlaceholder) ? (
            <div className="game-card__spinner-wrap" aria-hidden="true">
              <span className="game-card__spinner" />
            </div>
          ) : (
            <GameCardPlaceholder />
          )
        )}
      </div>
      <div className="game-card__bg-blur" aria-hidden="true" />
      <div className={`game-card__content-overlay${overlayOpen ? ' is-visible' : ''}`}>
        <div className="game-card__buttons" onClick={(e) => e.stopPropagation()}>
          {isTouchUi && game?.name ? (
            <div className="game-card__overlay-title" lang="en">{formatGameDisplayName(game.name)}</div>
          ) : null}
          <button type="button" className="game-card__play" onClick={openGame(false)}>
            {tr('casino.game.play')}
          </button>
          {isTouchUi ? (
            showDemo ? (
              <button type="button" className="game-card__demo" onClick={openGame(true)}>
                {tr('casino.game.demo')}
              </button>
            ) : null
          ) : (
            <div className="game-card__buttons-group">
              <button
                type="button"
                className={`game-card__favorite${isFavorite ? ' is-enabled' : ''}`}
                aria-label={tr('casino.game.favorite')}
                aria-pressed={isFavorite}
                onClick={toggleFavorite}
              >
                <IconStar filled={isFavorite} />
              </button>
              {showDemo ? (
                <button type="button" className="game-card__demo" onClick={openGame(true)}>
                  {tr('casino.game.demo')}
                </button>
              ) : null}
              <button
                type="button"
                className="game-card__popout"
                aria-label={tr('casino.game.popout')}
                onClick={openPopout}
              >
                <IconPopout />
              </button>
            </div>
          )}
        </div>
      </div>
      {visible && imgSrc && !imgFailed && (
        <img
          className="game-card__preload"
          src={imgSrc}
          alt=""
          width={resolvedIconSize === 'sm' ? 120 : 240}
          height={resolvedIconSize === 'sm' ? 163 : 326}
          loading={eagerImage ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={eagerImage ? 'high' : 'low'}
          onLoad={() => {
            markGameIconLoaded(imgSrc);
            setLoaded(true);
          }}
          onError={() => {
            setLoaded(false);
            if (!imgSrc || String(imgSrc).includes('_placeholder.webp') || forcePlaceholder) {
              setImgFailed(true);
              return;
            }
            markGameIconFailed(imgSrc);
            const nextIdx = candidateIdx + 1;
            if (nextIdx < candidates.length) {
              setCandidateIdx(nextIdx);
              setRetryTick((t) => t + 1);
              return;
            }
            if (spinnerWhileLoading) {
              setImgFailed(true);
              return;
            }
            setForcePlaceholder(true);
          }}
        />
      )}
      <div className="game-card__label" ref={labelRef}>
        <div className="game-card__caption" lang="en">
          <div className="game-card__caption-text">
            <GameCaption name={game.name} compact={compactCaption} />
            {settings.casinoProMode ? (
              <div className="game-card__pro-meta">
                <span>{formatProviderDisplayName(game.provider)}</span>
                <span>RTP {game.rtp || '96%'}</span>
              </div>
            ) : (
              <div className="game-card__caption-provider" title={game.provider}>
                {formatProviderDisplayName(game.provider)}
              </div>
            )}
          </div>
        </div>
      </div>
      {showHeat ? (
        <div className={`game-card__heat${isHeatBig ? ' game-card__heat--big' : ''}`} aria-hidden="true">
          <FlameHeatIcon id={heatId} big={isHeatBig} />
          <p className="game-card__heat-value">{heatValue}%</p>
        </div>
      ) : null}
      {(hotBadge || showHeat || badges.length > 0) && (
        <div
          className={[
            'game-card__tags-panel',
            hotBadge || showHeat ? 'game-card__tags-panel--stack' : '',
            showHeat && !isHeatBig ? 'game-card__tags-panel--shift' : '',
          ].filter(Boolean).join(' ')}
        >
          {hotBadge ? (
            <div className="game-card__tag game-card__tag--hot">
              <span className="game-card__tag-text">{tr('casino.badge.top')}</span>
            </div>
          ) : null}
          {badges.map((badge) => {
            const kind = String(badge.kind || badge || '').toLowerCase();
            const display = resolveBadgeDisplay(kind);
            let label = badge.label || null;
            if (!label && kind === 'tournament') label = tr('casino.badge.tournament');
            else if (!label && kind === 'new') label = tr('casino.badge.new');
            else if (!label && (kind === 'x50000' || kind === 'x15000')) label = kind;
            else if (!label) label = display?.label || null;
            if (kind === 'top' || kind === 'best' || /^top$/i.test(String(label || ''))) {
              label = tr('casino.badge.top');
            }
            if (!label) return null;
            const known = MAXLINE_TAG_KINDS.has(kind);
            const style = !known && display?.color
              ? { backgroundColor: display.color, color: '#fff' }
              : undefined;
            const className = [
              'game-card__tag',
              kind === 'tournament' ? 'game-card__tag--tournament' : '',
              kind === 'new' ? 'game-card__tag--new' : '',
              kind === 'x50000' ? 'game-card__tag--x50000' : '',
              kind === 'x15000' ? 'game-card__tag--x15000' : '',
              kind === 'fs' ? 'game-card__tag--freespin' : '',
              kind === 'hot' || kind === 'top' || kind === 'popular' || kind === 'best'
                ? 'game-card__tag--hot'
                : '',
            ].filter(Boolean).join(' ');
            return (
              <div key={`${kind}-${label}`} className={className} style={style}>
                <span className="game-card__tag-text">{label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
